import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2, Shield } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Inserisci la tua email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success(r.data?.message || "Richiesta inviata");
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between">
        <div>
          <img src="/logo-unioncasa-white.png" alt="UnionCasa" className="w-72 mb-6 select-none" />
          <h1 className="font-serif text-5xl font-light tracking-tight leading-tight">
            Recupera<br />l'accesso
          </h1>
          <p className="text-slate-400 mt-6 max-w-sm leading-relaxed">
            Ti invieremo un link sicuro per reimpostare la password del pannello amministratore.
          </p>
        </div>
        <div className="text-xs text-slate-500">Accesso riservato al personale autorizzato.</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-green-800 mb-3">
              <Shield size={14} /> Area Riservata
            </div>
            <h2 className="font-serif text-3xl text-slate-900 mb-2">Password dimenticata</h2>
            <p className="text-slate-500 text-sm">
              Inserisci la tua email amministratore: riceverai un link valido per 15 minuti.
            </p>
          </div>

          {sent ? (
            <div className="border border-emerald-200 bg-emerald-50 rounded-md p-4" data-testid="admin-forgot-sent">
              <div className="flex gap-3">
                <CheckCircle2 size={20} className="text-emerald-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-emerald-900">Controlla la casella email</p>
                  <p className="text-xs text-emerald-800 mt-1">
                    Se <strong>{email}</strong> è un account amministrativo valido, riceverai a breve il link per reimpostare la password.
                    Ricordati di controllare anche la cartella Spam.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="admin-forgot-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1.5">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    required className="pl-9" data-testid="admin-forgot-email" />
                </div>
              </div>
              <Button type="submit" disabled={busy}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="admin-forgot-submit">
                {busy ? "Invio in corso..." : "Invia link di reset"}
              </Button>
            </form>
          )}

          <p className="text-xs text-slate-400 mt-8 text-center">
            <Link to="/admin/login" className="inline-flex items-center gap-1 underline">
              <ArrowLeft size={12} /> Torna al login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
